import * as React from 'react';
import {
  EditProps as RaEditProps,
  Edit as RaEdit,
  useRedirect,
  useNotify,
  useTranslate,
  useUpdate,
} from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogContentText as MuiDialogContentText,
  DialogTitle as MuiDialogTitle,
} from '@material-ui/core';
import { Button as RaButton } from 'react-admin';
import { FunctionComponent, useCallback, useState } from 'react';
import ActionButtons from '../ActionButtons';
import Form from '../Form';
import { transformValuesBeforeSubmit } from '../../helpers/form';
import { handleError } from '../../helpers/error';

const useStyles = makeStyles({
  card: {
    boxShadow: 'none',
    background: 'none',
  },
});

const useScopeStyles = makeStyles({
  actions: {
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '16px',
  },
});

type SaveScopeOption = { value: string; label: string };

type PendingSave = {
  resource: Record<string, any>;
  record: Record<string, any>;
  values: Record<string, any>;
  redirect: string;
};

const Edit: FunctionComponent<RaEditProps> = (props) => {
  const classes = useStyles();
  const scopeClasses = useScopeStyles();
  const redirectTo = useRedirect();
  const notify = useNotify();
  const translate = useTranslate();
  const [update] = useUpdate();
  // Save held back until the user picks a scope (see EditPage#saveScope)
  const [pendingSave, setPendingSave] = useState<PendingSave | null>(null);

  const { basePath, resource: resourceName } = props;
  const page = 'edit';

  /// Override default behaviour to transform values before submit
  const save = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      resource: any,
      record: Record<string, any>,
      values: Record<string, any>,
      redirect: string
    ) => {
      // Transforms the base values
      let updatedValues = transformValuesBeforeSubmit(resource, values);
      // If resource = 'invoices', transform invoices_attachment_id to attachment_id
      if (record?.user_kind === 'expert' && updatedValues.attachments) {
        updatedValues = {
          ...updatedValues,
          attachment_id: updatedValues.invoices_attachments,
        };
        delete updatedValues.invoices_attachments;
      }

      // See EditPage#recordWarning (on load: see Form)
      const recordWarning = resource.pages.edit?.recordWarning;

      update(resource.name, values.id, updatedValues, record, {
        onSuccess: ({ data: newRecord }) => {
          if (recordWarning && newRecord?.[recordWarning.ifTrue]) {
            notify(recordWarning.message, 'warning');
          }

          // Reset dirty flags immediately on successful save so that action
          // buttons (e.g. VALIDER LA FACTURE) don't see stale dirty state
          // after the SPA redirect (the form component may not remount).
          // __reset_form_dirty__ also resets the userHasInteracted closure
          // variable inside FormStateListener; without it a subsequent
          // programmatic MUI change event would re-set dirty to true.
          const resetDirty = (window as any).__reset_form_dirty__;
          if (resetDirty) resetDirty();

          if ((window as any).__reload_after_save__) {
            delete (window as any).__reload_after_save__;
            window.location.reload();

            return;
          }
          let basePathName = basePath;
          let recordId = record.id;
          if (typeof resource.pages.edit.singleRedirect !== 'undefined') {
            const configRedirect = resource.pages.edit.singleRedirect;
            redirect = configRedirect.page;
            basePathName = configRedirect.basePath;
            recordId = record[configRedirect.recordId];
          }
          if (typeof resource.pages.edit.multipleRedirect !== 'undefined') {
            const configRedirect = resource.pages.edit.multipleRedirect.reduce(
              (acc: Record<string, any>, currentValue: Record<string, any>) => {
                if (currentValue.entityType === record.entity_type) {
                  acc = {
                    redirect: currentValue.page,
                    basePathName: currentValue.basePath,
                    recordId: record[currentValue.recordId],
                  };
                }

                return acc;
              },
              { redirect: '', basePathName: '' }
            );
            redirect = configRedirect.redirect;
            basePathName = configRedirect.basePathName;
            recordId = configRedirect.recordId;
          }
          redirectTo(redirect, basePathName, recordId, newRecord);
        },
        onFailure: (error: Record<string, any>) => {
          handleError(error, notify, 'notify.error.update', translate);
        },
      });
    },
    [redirectTo, basePath, update, transformValuesBeforeSubmit, notify]
  );

  const handleSave = useCallback(
    (resource, record) => (values: Record<string, any>, redirect: string) => {
      const saveScope = resource.pages.edit?.saveScope;
      const changed =
        saveScope &&
        (values[saveScope.field] ?? '') !== (record?.[saveScope.field] ?? '');
      if (changed) {
        setPendingSave({ resource, record, values, redirect });

        return;
      }

      save(resource, record, values, redirect);
    },
    [save]
  );

  const saveScope = pendingSave?.resource.pages.edit?.saveScope;

  const saveWithScope = (value: string) => {
    if (!pendingSave) return;

    const { resource, record, values, redirect } = pendingSave;
    setPendingSave(null);
    save(resource, record, { ...values, [saveScope.param]: value }, redirect);
  };

  return (
    <>
      <RaEdit
        {...props}
        classes={classes}
        undoable={false}
        actions={
          <ActionButtons
            basePath={basePath}
            resource={props.resource}
            page={page}
          />
        }
      >
        <Form
          {...props}
          page={page}
          handleSave={handleSave}
          resource={resourceName}
          withRight
        />
      </RaEdit>
      {saveScope && (
        <MuiDialog open onClose={() => setPendingSave(null)}>
          {saveScope.title && (
            <MuiDialogTitle>{translate(saveScope.title)}</MuiDialogTitle>
          )}
          <MuiDialogContent>
            <MuiDialogContentText>
              {translate(saveScope.content, pendingSave?.record)}
            </MuiDialogContentText>
          </MuiDialogContent>
          <MuiDialogActions className={scopeClasses.actions}>
            <RaButton
              onClick={() => setPendingSave(null)}
              variant="text"
              color="primary"
              label={translate('modal.confirm.cancel')}
            />
            {saveScope.options.map(({ value, label }: SaveScopeOption) => (
              <RaButton
                key={value}
                onClick={() => saveWithScope(value)}
                variant="contained"
                color="primary"
                label={translate(label)}
              />
            ))}
          </MuiDialogActions>
        </MuiDialog>
      )}
    </>
  );
};

export default Edit;
